/**
 * Logo component with animated layer switching
 * @module components/theme/Logo/Logo
 */
import { defineMessages, useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import LogoImage from '@plone/volto/components/theme/Logo/Logo.svg';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { getNavroot } from '@plone/volto/actions/navroot/navroot';
import { flattenToAppURL, getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import Image from '@plone/volto/components/theme/Image/Image';

/**
 * Animated FCMH Logo SVG Component
 */
const AnimatedFCMHLogo = ({ currentLayer = 1 }) => {
  return (
    <svg
      version="1.1"
      baseProfile="basic"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x="0px"
      y="0px"
      viewBox="0 0 500 500"
      xmlSpace="preserve"
      className="fcmh-animated-logo-svg"
    >
      <style type="text/css">
        {`
          .st0{fill-rule:evenodd;clip-rule:evenodd;fill:#1B130C;}
          .st1{fill:#1B130C;}
          .st2{fill:#008297;}
          .animation-layer {
            transition: opacity 0.3s ease-in-out;
          }
        `}
      </style>

      {/* Base logo text (FCMH) - always visible */}
      <g id="base-logo">
        <path className="st0" d="M101.9,200.6c0,0.7-0.2,1.3-0.7,1.8c-0.5,0.5-1.1,0.7-1.8,0.7h-68c-0.7,0-1,0.3-1,1v32.2c0,0.7,0.3,1,1,1h44.7
          c0.7,0,1.3,0.2,1.8,0.7c0.5,0.5,0.7,1.1,0.7,1.8V260c0,0.7-0.2,1.3-0.7,1.8s-1.1,0.7-1.8,0.7H31.5c-0.7,0-1,0.3-1,1v57.4
          c0,0.7-0.2,1.3-0.7,1.8c-0.5,0.5-1.1,0.7-1.8,0.7H3.6c-0.7,0-1.3-0.2-1.8-0.7s-0.7-1.1-0.7-1.8V180.4c0-0.7,0.2-1.3,0.7-1.8
          c0.5-0.5,1.1-0.7,1.8-0.7h95.8c0.7,0,1.3,0.2,1.8,0.7c0.5,0.5,0.7,1.1,0.7,1.8V200.6L101.9,200.6z"/>
        <path className="st0" d="M166.5,325.1c-10.8,0-20.3-2.1-28.5-6.3c-8.2-4.2-14.5-10.2-18.9-17.8c-4.4-7.6-6.7-16.4-6.7-26.4v-48
          c0-10,2.2-18.8,6.7-26.4c4.4-7.6,10.7-13.5,18.9-17.7c8.2-4.2,17.7-6.2,28.5-6.2c10.7,0,20.1,2,28.2,5.9
          c8.1,3.9,14.4,9.6,18.9,16.8c4.5,7.3,6.8,15.7,6.8,25.3c0,1.2-0.8,2-2.5,2.3l-24.3,1.5h-0.4c-1.4,0-2.1-0.8-2.1-2.3
          c0-7.3-2.3-13.2-6.8-17.7c-4.5-4.4-10.4-6.7-17.8-6.7c-7.5,0-13.5,2.2-18,6.7c-4.5,4.4-6.8,10.3-6.8,17.7v50.1
          c0,7.2,2.3,13,6.8,17.5c4.5,4.4,10.5,6.7,18,6.7c7.3,0,13.3-2.2,17.8-6.7c4.5-4.4,6.8-10.3,6.8-17.5c0-1.5,0.8-2.3,2.5-2.3l24.3,1
          c0.7,0,1.3,0.2,1.8,0.6c0.5,0.4,0.7,0.9,0.7,1.5c0,9.6-2.3,18-6.8,25.4c-4.5,7.3-10.8,13-18.9,17
          C186.5,323.1,177.2,325.1,166.5,325.1L166.5,325.1z"/>
        <path className="st0" d="M335.3,179.6c0.7-1.1,1.7-1.7,2.9-1.7h24.3c0.7,0,1.3,0.2,1.8,0.7c0.5,0.5,0.7,1.1,0.7,1.8v140.5
          c0,0.7-0.2,1.3-0.7,1.8c-0.5,0.5-1.1,0.7-1.8,0.7h-24.3c-0.7,0-1.3-0.2-1.8-0.7c-0.5-0.5-0.7-1.1-0.7-1.8v-91.7
          c0-0.6-0.1-0.8-0.4-0.8s-0.6,0.2-0.8,0.6l-22,34.5c-0.7,1.1-1.7,1.7-2.9,1.7h-12.3c-1.2,0-2.2-0.6-2.9-1.7l-22-34.5
          c-0.3-0.4-0.6-0.6-0.8-0.5c-0.3,0.1-0.4,0.4-0.4,0.9v91.5c0,0.7-0.2,1.3-0.7,1.8c-0.5,0.5-1.1,0.7-1.8,0.7h-24.3
          c-0.7,0-1.3-0.2-1.8-0.7c-0.5-0.5-0.7-1.1-0.7-1.8V180.4c0-0.7,0.2-1.3,0.7-1.8c0.5-0.5,1.1-0.7,1.8-0.7h24.3
          c1.2,0,2.2,0.6,2.9,1.7l31.2,48.4c0.4,0.8,0.8,0.8,1.2,0L335.3,179.6L335.3,179.6z"/>
        <path className="st0" d="M469.6,180.4c0-0.7,0.2-1.3,0.7-1.8c0.5-0.5,1.1-0.7,1.8-0.7h24.3c0.7,0,1.3,0.2,1.8,0.7
          c0.5,0.5,0.7,1.1,0.7,1.8v140.5c0,0.7-0.2,1.3-0.7,1.8s-1.1,0.7-1.8,0.7h-24.3c-0.7,0-1.3-0.2-1.8-0.7c-0.5-0.5-0.7-1.1-0.7-1.8
          v-57.4c0-0.7-0.3-1-1-1h-45.9c-0.7,0-1,0.3-1,1v57.4c0,0.7-0.2,1.3-0.7,1.8c-0.5,0.5-1.1,0.7-1.8,0.7h-24.3c-0.7,0-1.3-0.2-1.8-0.7
          c-0.5-0.5-0.7-1.1-0.7-1.8V180.4c0-0.7,0.2-1.3,0.7-1.8c0.5-0.5,1.1-0.7,1.8-0.7h24.3c0.7,0,1.3,0.2,1.8,0.7
          c0.5,0.5,0.7,1.1,0.7,1.8v55.9c0,0.7,0.3,1,1,1h45.9c0.7,0,1-0.3,1-1V180.4z"/>
      </g>

      {/* Animation Layers (1-10) with conditional opacity */}
      {/* Layer 1 */}
      <g id="animation-layer-1" className="animation-layer" opacity={currentLayer === 1 ? 1 : 0}>
        <path className="st1" d="M4.4,0h491.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V4.4l0,0C0.3,2.1,2.1,0.3,4.4,0z"/>
        <path className="st1" d="M254.4,59.3h241.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H254.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V63.7l0,0C250.3,61.4,252.1,59.5,254.4,59.3z"/>
        <path className="st2" d="M4.4,118.6h324.5l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V123l0,0C0.3,120.7,2.1,118.8,4.4,118.6z"/>
      </g>

      {/* Layer 2 */}
      <g id="animation-layer-2" className="animation-layer" opacity={currentLayer === 2 ? 1 : 0}>
        <path className="st1" d="M171.1,0h324.5l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H171.1l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V4.4l0,0C166.9,2.1,168.8,0.3,171.1,0z"/>
        <path className="st1" d="M4.4,59.3h157.8l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V63.7l0,0C0.3,61.4,2.1,59.5,4.4,59.3z"/>
        <path className="st2" d="M4.4,118.6h491.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V123l0,0C0.3,120.7,2.1,118.8,4.4,118.6z"/>
      </g>

      {/* Layer 3 */}
      <g id="animation-layer-3" className="animation-layer" opacity={currentLayer === 3 ? 1 : 0}>
        <path className="st2" d="M337.8,0h157.8c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H337.8c-2.3-0.3-4.2-2.1-4.4-4.4
          V4.4l0,0C333.7,2.1,335.5,0.2,337.8,0z"/>
        <path className="st1" d="M4.4,59.3h491.1c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4c-2.3-0.3-4.2-2.1-4.4-4.4
          V63.7l0,0C0.3,61.4,2.1,59.5,4.4,59.3z"/>
        <path className="st1" d="M4.4,118.6h324.5c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4c-2.3-0.3-4.2-2.1-4.4-4.4
          V123l0,0C0.3,120.7,2.1,118.8,4.4,118.6z"/>
      </g>

      {/* Layer 4 */}
      <g id="animation-layer-4" className="animation-layer" opacity={currentLayer === 4 ? 1 : 0}>
        <path className="st2" d="M4.4,0h491.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V4.4l0,0C0.3,2.1,2.1,0.3,4.4,0z"/>
        <path className="st1" d="M171.1,59.3h324.5l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H171.1l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V63.7l0,0C166.9,61.4,168.8,59.5,171.1,59.3z"/>
        <path className="st1" d="M4.4,118.6h157.8l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V123l0,0C0.3,120.7,2.1,118.8,4.4,118.6z"/>
      </g>

      {/* Layer 5 */}
      <g id="animation-layer-5" className="animation-layer" opacity={currentLayer === 5 ? 1 : 0}>
        <path className="st1" d="M254.4,0h241.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H254.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V4.4l0,0C250.3,2.1,252.1,0.3,254.4,0z"/>
        <path className="st2" d="M4.4,59.3h491.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V63.7l0,0C0.3,61.4,2.1,59.5,4.4,59.3z"/>
        <path className="st1" d="M4.4,118.6h324.5l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V123l0,0C0.3,120.7,2.1,118.8,4.4,118.6z"/>
      </g>

      {/* Layer 6 */}
      <g id="animation-layer-6" className="animation-layer" opacity={currentLayer === 6 ? 1 : 0}>
        <path className="st1" d="M171.1,0h324.5l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H171.1l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V4.4l0,0C166.9,2.1,168.8,0.3,171.1,0z"/>
        <path className="st1" d="M4.4,59.3h157.8l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V63.7l0,0C0.3,61.4,2.1,59.5,4.4,59.3z"/>
        <path className="st2" d="M4.4,118.6h491.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V123l0,0C0.3,120.7,2.1,118.8,4.4,118.6z"/>
      </g>

      {/* Layer 7 */}
      <g id="animation-layer-7" className="animation-layer" opacity={currentLayer === 7 ? 1 : 0}>
        <path className="st1" d="M4.4,0h491.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V4.4l0,0C0.3,2.1,2.1,0.3,4.4,0z"/>
        <path className="st1" d="M171.1,59.3h324.5l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H171.1l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V63.7l0,0C166.9,61.4,168.8,59.5,171.1,59.3z"/>
        <path className="st2" d="M4.4,118.6h157.8l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V123l0,0C0.3,120.7,2.1,118.8,4.4,118.6z"/>
      </g>

      {/* Layer 8 */}
      <g id="animation-layer-8" className="animation-layer" opacity={currentLayer === 8 ? 1 : 0}>
        <path className="st1" d="M337.8,0h157.8l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H337.8l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V4.4l0,0C333.6,2.1,335.5,0.3,337.8,0z"/>
        <path className="st2" d="M4.4,59.3h241.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V63.7l0,0C0.3,61.4,2.1,59.5,4.4,59.3z"/>
        <path className="st1" d="M4.4,118.6h491.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V123l0,0C0.3,120.7,2.1,118.8,4.4,118.6z"/>
      </g>

      {/* Layer 9 */}
      <g id="animation-layer-9" className="animation-layer" opacity={currentLayer === 9 ? 1 : 0}>
        <path className="st1" d="M4.4,0h491.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V4.4l0,0C0.3,2.1,2.1,0.3,4.4,0z"/>
        <path className="st1" d="M337.8,59.3h157.8l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H337.8l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V63.7l0,0C333.6,61.4,335.5,59.5,337.8,59.3z"/>
        <path className="st2" d="M4.4,118.6h241.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V123l0,0C0.3,120.7,2.1,118.8,4.4,118.6z"/>
      </g>

      {/* Layer 10 */}
      <g id="animation-layer-10" className="animation-layer" opacity={currentLayer === 10 ? 1 : 0}>
        <path className="st1" d="M254.4,0h241.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H254.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V4.4l0,0C250.3,2.1,252.1,0.3,254.4,0z"/>
        <path className="st1" d="M4.4,59.3h491.1l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V63.7l0,0C0.3,61.4,2.1,59.5,4.4,59.3z"/>
        <path className="st2" d="M4.4,118.6h157.8l0,0c2.3,0.3,4.2,2.1,4.4,4.4v20.8l0,0c-0.3,2.3-2.1,4.2-4.4,4.4H4.4l0,0
          c-2.3-0.3-4.2-2.1-4.4-4.4V123l0,0C0.3,120.7,2.1,118.8,4.4,118.6z"/>
      </g>

      {/* Bottom text content - always visible */}
      <g id="bottom-text">
        <path d="M85.5,356.6h-9.7c-0.1,0-0.2,0.1-0.2,0.2v6.6c0,0.1,0.1,0.2,0.2,0.2h6.2c0.2,0,0.4,0.2,0.4,0.4v2.7c0,0.2-0.2,0.4-0.4,0.4
          h-6.2c-0.1,0-0.2,0.1-0.2,0.2v10c0,0.2-0.2,0.4-0.4,0.4H72c-0.2,0-0.4-0.2-0.4-0.4v-23.7c0-0.2,0.2-0.4,0.4-0.4h13.5
          c0.2,0,0.4,0.2,0.4,0.4v2.7C86,356.4,85.8,356.6,85.5,356.6z"/>
        <path d="M92,370.3v-9.9c0-4.6,3.1-7.6,7.8-7.6c4.7,0,7.8,3,7.8,7.6v9.9c0,4.6-3.1,7.6-7.8,7.6C95.1,377.9,92,374.9,92,370.3z
          M103.6,370.5v-10.2c0-2.4-1.5-4-3.8-4c-2.2,0-3.7,1.6-3.7,4v10.2c0,2.3,1.5,4,3.7,4C102.1,374.4,103.6,372.8,103.6,370.5z"/>
        <path d="M126.6,377.3l-3.8-10.1c0-0.1-0.1-0.1-0.2-0.1h-3c-0.1,0-0.2,0.1-0.2,0.2v10c0,0.2-0.2,0.4-0.4,0.4h-3.2
          c-0.2,0-0.4-0.2-0.4-0.4v-23.7c0-0.2,0.2-0.4,0.4-0.4h8.1c4.1,0,6.8,2.9,6.8,7.1c0,3.1-1.5,5.4-3.9,6.3c-0.1,0-0.1,0.1-0.1,0.2
          l4.1,10.3c0.1,0.3,0,0.5-0.3,0.5h-3.4C126.9,377.6,126.7,377.5,126.6,377.3z M119.5,356.8v7c0,0.1,0.1,0.2,0.2,0.2h3.7
          c2,0,3.3-1.4,3.3-3.6c0-2.3-1.3-3.7-3.3-3.7h-3.7C119.6,356.6,119.5,356.6,119.5,356.8z"/>
        <path d="M137.4,371.2v-0.9c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.2,0.4,0.4v0.7c0,2,1.5,3.4,3.9,3.4c2.2,0,3.4-1.3,3.4-3
          c0-2-1.5-2.8-4.7-4.4c-3.3-1.7-6.5-3.4-6.5-7.6c0-4.1,2.9-6.5,7.3-6.5c4.6,0,7.7,2.8,7.7,6.9v0.7c0,0.2-0.2,0.4-0.4,0.4h-3.2
          c-0.2,0-0.4-0.2-0.4-0.4v-0.7c0-2-1.4-3.5-3.7-3.5c-2,0-3.2,1.1-3.2,3c0,1.9,1.3,2.7,4.6,4.4c3.6,2,6.5,3.5,6.5,7.5
          c0,4.1-3,6.7-7.6,6.7C140.4,377.9,137.4,375.2,137.4,371.2z"/>
        <path d="M159.5,370.5v-10.3c0-4.4,3.1-7.4,7.7-7.4s7.7,2.8,7.7,7.2v0.2c0,0.2-0.2,0.5-0.4,0.5l-3.3,0.1c-0.2,0-0.4-0.2-0.4-0.4
          V360c0-2.2-1.4-3.7-3.6-3.7c-2.2,0-3.6,1.5-3.6,3.7v10.7c0,2.2,1.4,3.6,3.6,3.6c2.2,0,3.6-1.5,3.6-3.6v-0.5c0-0.3,0.1-0.4,0.4-0.4
          l3.3,0.1c0.2,0,0.4,0.1,0.4,0.3v0.3c0,4.4-3.1,7.3-7.7,7.3S159.5,375,159.5,370.5z"/>
        <path d="M194,353.1h3.2c0.2,0,0.4,0.2,0.4,0.4v23.7c0,0.2-0.2,0.4-0.4,0.4H194c-0.2,0-0.4-0.2-0.4-0.4v-9.9c0-0.1-0.1-0.2-0.2-0.2
          h-6.7c-0.1,0-0.2,0.1-0.2,0.2v9.9c0,0.2-0.2,0.4-0.4,0.4h-3.2c-0.2,0-0.4-0.2-0.4-0.4v-23.7c0-0.2,0.2-0.4,0.4-0.4h3.2
          c0.2,0,0.4,0.2,0.4,0.4v9.8c0,0.1,0.1,0.2,0.2,0.2h6.7c0.1,0,0.2-0.1,0.2-0.2v-9.8C193.6,353.3,193.8,353.1,194,353.1z"/>
        <path d="M205.6,370.3v-16.7c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.2,0.4,0.4v16.9c0,2.3,1.5,3.9,3.6,3.9c2.2,0,3.7-1.6,3.7-3.9
          v-16.9c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.2,0.4,0.4v16.7c0,4.6-3.1,7.6-7.7,7.6C208.7,377.9,205.6,374.8,205.6,370.3z"/>
        <path d="M229.1,377.2v-23.7c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.1,0.5,0.3l7.7,15.2c0.1,0.2,0.2,0.1,0.2,0v-15.1
          c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.2,0.4,0.4v23.7c0,0.2-0.2,0.4-0.4,0.4h-3.2c-0.2,0-0.4-0.1-0.5-0.3l-7.7-15.3
          c-0.1-0.2-0.2-0.1-0.2,0l0,15.2c0,0.2-0.2,0.4-0.4,0.4h-3.2C229.2,377.6,229.1,377.4,229.1,377.2z"/>
        <path d="M252.9,370.6v-10.4c0-4.4,3.1-7.3,7.7-7.3c4.6,0,7.7,2.9,7.7,7.1v0.8c0,0.2-0.2,0.4-0.4,0.4h-3.2c-0.2,0-0.4-0.2-0.4-0.4
          v-0.8c0-2.1-1.4-3.6-3.6-3.6c-2.2,0-3.6,1.5-3.6,3.7v10.6c0,2.2,1.5,3.7,3.7,3.7c2.1,0,3.5-1.3,3.5-3.5v-2.8
          c0-0.1-0.1-0.2-0.2-0.2h-3.2c-0.2,0-0.4-0.2-0.4-0.4V365c0-0.2,0.2-0.4,0.4-0.4h7c0.2,0,0.4,0.2,0.4,0.4v5.5
          c0,4.7-3.1,7.4-7.7,7.4C255.9,377.9,252.9,375,252.9,370.6z"/>
        <path d="M275.1,371.2v-0.9c0-0.2,0.2-0.4,0.4-0.4h3.1c0.2,0,0.4,0.2,0.4,0.4v0.7c0,2,1.5,3.4,3.8,3.4c2.2,0,3.4-1.3,3.4-3
          c0-2-1.5-2.8-4.7-4.4c-3.3-1.7-6.5-3.4-6.5-7.6c0-4.1,2.9-6.5,7.3-6.5c4.6,0,7.7,2.8,7.7,6.9v0.7c0,0.2-0.2,0.4-0.4,0.4h-3.2
          c-0.2,0-0.4-0.2-0.4-0.4v-0.7c0-2-1.4-3.5-3.7-3.5c-2,0-3.2,1.1-3.2,3c0,1.9,1.3,2.7,4.6,4.4c3.6,2,6.5,3.5,6.5,7.5
          c0,4.1-3,6.7-7.6,6.7C278.1,377.9,275.1,375.2,275.1,371.2z"/>
        <path d="M297.2,370.5v-10.3c0-4.4,3.1-7.4,7.7-7.4s7.7,2.8,7.7,7.2v0.2c0,0.2-0.2,0.5-0.4,0.5l-3.3,0.1c-0.2,0-0.4-0.2-0.4-0.4
          V360c0-2.2-1.4-3.7-3.6-3.7c-2.2,0-3.6,1.5-3.6,3.7v10.7c0,2.2,1.4,3.6,3.6,3.6c2.2,0,3.6-1.5,3.6-3.6v-0.5c0-0.3,0.1-0.4,0.4-0.4
          l3.3,0.1c0.2,0,0.4,0.1,0.4,0.3v0.3c0,4.4-3.1,7.3-7.7,7.3S297.2,375,297.2,370.5z"/>
        <path d="M332.2,377.3l-0.9-3.7c0-0.1-0.1-0.1-0.2-0.1H324c-0.1,0-0.1,0-0.2,0.1l-0.9,3.7c-0.1,0.2-0.2,0.3-0.5,0.3h-3.3
          c-0.3,0-0.4-0.1-0.4-0.5l6.3-23.7c0.1-0.2,0.2-0.4,0.5-0.4h4c0.2,0,0.4,0.1,0.5,0.4l6.4,23.7c0.1,0.3-0.1,0.5-0.3,0.5h-3.4
          C332.5,377.6,332.3,377.5,332.2,377.3z M324.9,370.2h5.5c0.1,0,0.2-0.1,0.2-0.2l-2.8-11.3c0-0.1-0.2-0.1-0.2,0l-2.8,11.3
          C324.6,370.1,324.8,370.2,324.9,370.2z"/>
        <path d="M343.6,377.2v-23.7c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.1,0.5,0.3l4.4,8.6c0.1,0.1,0.1,0.1,0.2,0l4.5-8.6
          c0.1-0.2,0.3-0.3,0.5-0.3h3.2c0.2,0,0.4,0.2,0.4,0.4v23.7c0,0.2-0.2,0.4-0.4,0.4h-3.2c-0.2,0-0.4-0.2-0.4-0.4v-15.8
          c0-0.2-0.1-0.2-0.2,0l-3.3,6.4c-0.1,0.2-0.3,0.3-0.5,0.3h-1.2c-0.2,0-0.4-0.1-0.5-0.3l-3.3-6.4c-0.1-0.2-0.2-0.1-0.2,0v15.8
          c0,0.2-0.2,0.4-0.4,0.4H344C343.8,377.6,343.6,377.4,343.6,377.2z"/>
        <path d="M384.2,360.4c0,4.3-2.8,7.1-6.8,7.1h-4.3c-0.1,0-0.2,0.1-0.2,0.2v9.6c0,0.2-0.2,0.4-0.4,0.4h-3.2c-0.2,0-0.4-0.2-0.4-0.4
          v-23.7c0-0.2,0.2-0.4,0.4-0.4h8.1C381.5,353.1,384.2,356,384.2,360.4z M380.2,360.4c0-2.3-1.4-3.9-3.4-3.9h-3.7
          c-0.1,0-0.2,0.1-0.2,0.2v7.3c0,0.1,0.1,0.2,0.2,0.2h3.7C378.8,364.2,380.2,362.7,380.2,360.4z"/>
        <path d="M391,370.3v-16.7c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.2,0.4,0.4v16.9c0,2.3,1.5,3.9,3.6,3.9c2.2,0,3.7-1.6,3.7-3.9
          v-16.9c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.2,0.4,0.4v16.7c0,4.6-3.1,7.6-7.7,7.6C394.1,377.9,391,374.8,391,370.3z"/>
        <path d="M413.8,371.2v-0.9c0-0.2,0.2-0.4,0.4-0.4h3.1c0.2,0,0.4,0.2,0.4,0.4v0.7c0,2,1.5,3.4,3.8,3.4c2.2,0,3.4-1.3,3.4-3
          c0-2-1.5-2.8-4.7-4.4c-3.3-1.7-6.5-3.4-6.5-7.6c0-4.1,2.9-6.5,7.3-6.5c4.6,0,7.7,2.8,7.7,6.9v0.7c0,0.2-0.2,0.4-0.4,0.4h-3.2
          c-0.2,0-0.4-0.2-0.4-0.4v-0.7c0-2-1.4-3.5-3.7-3.5c-2,0-3.2,1.1-3.2,3c0,1.9,1.3,2.7,4.6,4.4c3.6,2,6.5,3.5,6.5,7.5
          c0,4.1-3,6.7-7.6,6.7C416.9,377.9,413.8,375.2,413.8,371.2z"/>
        <path d="M124.2,422.2v-23.7c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.1,0.5,0.3l4.4,8.6c0.1,0.1,0.1,0.1,0.2,0l4.5-8.6
          c0.1-0.2,0.3-0.3,0.5-0.3h3.2c0.2,0,0.4,0.2,0.4,0.4v23.7c0,0.2-0.2,0.4-0.4,0.4h-3.2c-0.2,0-0.4-0.2-0.4-0.4v-15.8
          c0-0.2-0.1-0.2-0.2,0l-3.3,6.4c-0.1,0.2-0.3,0.3-0.5,0.3h-1.2c-0.2,0-0.4-0.1-0.5-0.3l-3.3-6.4c-0.1-0.2-0.2-0.1-0.2,0v15.8
          c0,0.2-0.2,0.4-0.4,0.4h-3.2C124.4,422.6,124.2,422.4,124.2,422.2z"/>
        <path d="M149.8,422.2v-23.7c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.2,0.4,0.4v23.7c0,0.2-0.2,0.4-0.4,0.4h-3.2
          C150,422.6,149.8,422.4,149.8,422.2z"/>
        <path d="M176.9,398.5v2.7c0,0.2-0.2,0.4-0.4,0.4h-5.4c-0.1,0-0.2,0.1-0.2,0.2v20.4c0,0.2-0.2,0.4-0.4,0.4h-3.2
          c-0.2,0-0.4-0.2-0.4-0.4v-20.4c0-0.1-0.1-0.2-0.2-0.2h-5.2c-0.2,0-0.4-0.2-0.4-0.4v-2.7c0-0.2,0.2-0.4,0.4-0.4h15.1
          C176.8,398.1,176.9,398.3,176.9,398.5z"/>
        <path d="M198.8,398.5v2.7c0,0.2-0.2,0.4-0.4,0.4H193c-0.1,0-0.2,0.1-0.2,0.2v20.4c0,0.2-0.2,0.4-0.4,0.4h-3.2
          c-0.2,0-0.4-0.2-0.4-0.4v-20.4c0-0.1-0.1-0.2-0.2-0.2h-5.2c-0.2,0-0.4-0.2-0.4-0.4v-2.7c0-0.2,0.2-0.4,0.4-0.4h15.1
          C198.7,398.1,198.8,398.3,198.8,398.5z"/>
        <path d="M220,401.6h-9.7c-0.1,0-0.2,0.1-0.2,0.2v6.6c0,0.1,0.1,0.2,0.2,0.2h6.2c0.2,0,0.4,0.2,0.4,0.4v2.7c0,0.2-0.2,0.4-0.4,0.4
          h-6.2c-0.1,0-0.2,0.1-0.2,0.2v6.7c0,0.1,0.1,0.2,0.2,0.2h9.7c0.2,0,0.4,0.2,0.4,0.4v2.7c0,0.2-0.2,0.4-0.4,0.4h-13.5
          c-0.2,0-0.4-0.2-0.4-0.4v-23.7c0-0.2,0.2-0.4,0.4-0.4H220c0.2,0,0.4,0.2,0.4,0.4v2.7C220.4,401.4,220.2,401.6,220,401.6z"/>
        <path d="M227.8,422.2v-23.7c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.2,0.4,0.4v20.4c0,0.1,0.1,0.2,0.2,0.2h9.6
          c0.2,0,0.4,0.2,0.4,0.4v2.7c0,0.2-0.2,0.4-0.4,0.4h-13.4C228,422.6,227.8,422.4,227.8,422.2z"/>
        <path d="M260.4,398.1h3.2c0.2,0,0.4,0.2,0.4,0.4v23.7c0,0.2-0.2,0.4-0.4,0.4h-3.2c-0.2,0-0.4-0.2-0.4-0.4v-9.9
          c0-0.1-0.1-0.2-0.2-0.2h-6.7c-0.1,0-0.2,0.1-0.2,0.2v9.9c0,0.2-0.2,0.4-0.4,0.4h-3.2c-0.2,0-0.4-0.2-0.4-0.4v-23.7
          c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.2,0.4,0.4v9.8c0,0.1,0.1,0.2,0.2,0.2h6.7c0.1,0,0.2-0.1,0.2-0.2v-9.8
          C260,398.3,260.2,398.1,260.4,398.1z"/>
        <path d="M286.2,401.6h-9.7c-0.1,0-0.2,0.1-0.2,0.2v6.6c0,0.1,0.1,0.2,0.2,0.2h6.2c0.2,0,0.4,0.2,0.4,0.4v2.7
          c0,0.2-0.2,0.4-0.4,0.4h-6.2c-0.1,0-0.2,0.1-0.2,0.2v6.7c0,0.1,0.1,0.2,0.2,0.2h9.7c0.2,0,0.4,0.2,0.4,0.4v2.7
          c0,0.2-0.2,0.4-0.4,0.4h-13.5c-0.2,0-0.4-0.2-0.4-0.4v-23.7c0-0.2,0.2-0.4,0.4-0.4h13.5c0.2,0,0.4,0.2,0.4,0.4v2.7
          C286.6,401.4,286.4,401.6,286.2,401.6z"/>
        <path d="M293.2,416.2v-0.9c0-0.2,0.2-0.4,0.4-0.4h3.1c0.2,0,0.4,0.2,0.4,0.4v0.7c0,2,1.5,3.4,3.8,3.4c2.2,0,3.4-1.3,3.4-3
          c0-2-1.5-2.8-4.7-4.4c-3.3-1.7-6.5-3.4-6.5-7.6c0-4.1,2.9-6.5,7.3-6.5c4.6,0,7.7,2.8,7.7,6.9v0.7c0,0.2-0.2,0.4-0.4,0.4h-3.2
          c-0.2,0-0.4-0.2-0.4-0.4v-0.7c0-2-1.4-3.5-3.7-3.5c-2,0-3.2,1.1-3.2,3c0,1.9,1.3,2.7,4.6,4.4c3.6,2,6.5,3.5,6.5,7.5
          c0,4.1-3,6.7-7.6,6.7C296.3,422.9,293.2,420.2,293.2,416.2z"/>
        <path d="M315.1,416.2v-0.9c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.2,0.4,0.4v0.7c0,2,1.5,3.4,3.8,3.4c2.2,0,3.4-1.3,3.4-3
          c0-2-1.5-2.8-4.7-4.4c-3.3-1.7-6.5-3.4-6.5-7.6c0-4.1,2.9-6.5,7.3-6.5c4.6,0,7.7,2.8,7.7,6.9v0.7c0,0.2-0.2,0.4-0.4,0.4h-3.2
          c-0.2,0-0.4-0.2-0.4-0.4v-0.7c0-2-1.4-3.5-3.7-3.5c-2,0-3.2,1.1-3.2,3c0,1.9,1.3,2.7,4.6,4.4c3.6,2,6.5,3.5,6.5,7.5
          c0,4.1-3,6.7-7.6,6.7C318.2,422.9,315.1,420.2,315.1,416.2z"/>
        <path d="M351.7,401.6h-9.7c-0.1,0-0.2,0.1-0.2,0.2v6.6c0,0.1,0.1,0.2,0.2,0.2h6.2c0.2,0,0.4,0.2,0.4,0.4v2.7
          c0,0.2-0.2,0.4-0.4,0.4h-6.2c-0.1,0-0.2,0.1-0.2,0.2v6.7c0,0.1,0.1,0.2,0.2,0.2h9.7c0.2,0,0.4,0.2,0.4,0.4v2.7
          c0,0.2-0.2,0.4-0.4,0.4h-13.5c-0.2,0-0.4-0.2-0.4-0.4v-23.7c0-0.2,0.2-0.4,0.4-0.4h13.5c0.2,0,0.4,0.2,0.4,0.4v2.7
          C352.1,401.4,351.9,401.6,351.7,401.6z"/>
        <path d="M359.5,422.2v-23.7c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.1,0.5,0.3l7.7,15.2c0.1,0.2,0.2,0.1,0.2,0v-15.1
          c0-0.2,0.2-0.4,0.4-0.4h3.2c0.2,0,0.4,0.2,0.4,0.4v23.7c0,0.2-0.2,0.4-0.4,0.4h-3.2c-0.2,0-0.4-0.1-0.5-0.3l-7.7-15.3
          c-0.1-0.2-0.2-0.1-0.2,0l0,15.2c0,0.2-0.2,0.4-0.4,0.4h-3.2C359.7,422.6,359.5,422.4,359.5,422.2z"/>
      </g>
    </svg>
  );
};

/**
 * Logo component with animated layer switching on navigation clicks
 * @function Logo
 * @returns {string} Markup of the component.
 */
const Logo = () => {
  const pathname = useLocation().pathname;
  const site = useSelector((state) => state.site.data);
  const navroot = useSelector((state) => state.navroot.data);
  const dispatch = useDispatch();
  const intl = useIntl();
  const [currentLayer, setCurrentLayer] = useState(1);
  const currentLayerRef = React.useRef(currentLayer);

  const messages = defineMessages({
    home: {
      id: 'Home',
      defaultMessage: 'Home',
    },
    logoOf: {
      id: 'Logo of',
      defaultMessage: 'Logo of',
    },
  });

  useEffect(() => {
    if (pathname && !hasApiExpander('navroot', getBaseUrl(pathname))) {
      dispatch(getNavroot(getBaseUrl(pathname)));
    }
  }, [dispatch, pathname]);

  // Keep ref in sync with state
  useEffect(() => {
    currentLayerRef.current = currentLayer;
  }, [currentLayer]);

  // Initialize current layer from sessionStorage
  useEffect(() => {
    const savedLayer = sessionStorage.getItem('fcmh-logo-layer');
    if (savedLayer && savedLayer >= 1 && savedLayer <= 10) {
      setCurrentLayer(parseInt(savedLayer, 10));
    }
  }, []);

  // Listen for navigation click events directly
  useEffect(() => {
    const handleNavigationClick = (event) => {
      // Check if the click is on a navigation item
      const navLink = event.target.closest('.navigation a');
      if (navLink) {
        // Generate a random layer number (1-10), excluding current
        const current = currentLayerRef.current;
        const availableLayers = Array.from({ length: 10 }, (_, i) => i + 1).filter(
          (layer) => layer !== current
        );
        const newLayer = availableLayers[Math.floor(Math.random() * availableLayers.length)];

        setCurrentLayer(newLayer);
        sessionStorage.setItem('fcmh-logo-layer', newLayer.toString());
      }
    };

    // Listen for clicks on the document
    document.addEventListener('click', handleNavigationClick);

    // Also listen for the custom event (in case NavItems is working)
    const handleCustomEvent = () => {
      // Generate a random layer number (1-10), excluding current
      const current = currentLayerRef.current;
      const availableLayers = Array.from({ length: 10 }, (_, i) => i + 1).filter(
        (layer) => layer !== current
      );
      const newLayer = availableLayers[Math.floor(Math.random() * availableLayers.length)];

      setCurrentLayer(newLayer);
      sessionStorage.setItem('fcmh-logo-layer', newLayer.toString());
    };

    document.addEventListener('fcmh-navigation-click', handleCustomEvent);

    return () => {
      document.removeEventListener('click', handleNavigationClick);
      document.removeEventListener('fcmh-navigation-click', handleCustomEvent);
    };
  }, []);

  const navRootPath = flattenToAppURL(navroot?.navroot?.['@id']) || '/';

  // Always use our animated logo for FCMH
  const isAnimatedLogo = true;

  return (
    <Link to={navRootPath} aria-label={intl.formatMessage(messages.home)}>
      {isAnimatedLogo ? (
        <div className="ui image fcmh-animated-logo">
          <AnimatedFCMHLogo currentLayer={currentLayer} />
        </div>
      ) : (
        // Fallback to standard Image component for non-animated logos
        <Image
          className="ui image"
          src={
            site['plone.site_logo']
              ? flattenToAppURL(site['plone.site_logo'])
              : LogoImage
          }
          alt={
            intl.formatMessage(messages.logoOf) + ' ' + site['plone.site_title']
          }
        />
      )}
    </Link>
  );
};

export default Logo;